import React from "react";

/**
 * Temporary design preview page — shows 5 visual variants side-by-side.
 * Each variant renders a small mock of: eyebrow + title + score + type + CTA.
 * All inline-styled to avoid touching global tokens.
 */

const FONTS_LINK = (
  <link
    rel="stylesheet"
    href="https://fonts.googleapis.com/css2?family=VT323&family=JetBrains+Mono:wght@400;700&family=Audiowide&family=Shippori+Mincho:wght@600;800&family=Crimson+Text:wght@600;700&family=Inter:wght@400;600;700;800;900&family=Major+Mono+Display&family=Cormorant+Garamond:wght@400;700&family=Bodoni+Moda:ital,wght@0,400;0,700;0,900;1,400&family=Press+Start+2P&family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;600;700&family=Roboto+Mono:wght@400;500;700&family=Noto+Sans+JP:wght@400;700;900&family=Space+Mono:wght@400;700&display=swap"
  />
);

const Card = ({ label, children, bg, fg, mono }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: "#888",
      }}
    >
      // {label}
    </div>
    <div
      style={{
        background: bg,
        color: fg,
        padding: 32,
        minHeight: 360,
        position: "relative",
        overflow: "hidden",
        fontFamily: mono || "Inter, sans-serif",
        border: "1px solid #1A2025",
      }}
    >
      {children}
    </div>
  </div>
);

export default function DesignPreview() {
  return (
    <>
      {FONTS_LINK}
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "48px 32px",
          background: "#060809",
          color: "#fff",
          minHeight: "100vh",
        }}
      >
        <h1
          style={{
            fontFamily: "Anton, sans-serif",
            fontSize: 64,
            letterSpacing: "-0.02em",
            margin: 0,
            textTransform: "uppercase",
          }}
        >
          Design Variants
        </h1>
        <p style={{ color: "#8E9CA8", marginTop: 8, marginBottom: 40 }}>
          5 visual directions for IQ Log — pick the one you like best.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
            gap: 32,
          }}
        >
          {/* A — Bloomberg Terminal */}
          <Card label="A · Bloomberg Terminal" bg="#000000" fg="#FFB000">
            <div
              style={{
                fontFamily: "'VT323', monospace",
                fontSize: 14,
                letterSpacing: "0.1em",
                color: "#00FFFF",
                marginBottom: 18,
              }}
            >
              ◆ DIAGNOSTIC // RESULT 092384
            </div>
            <div
              style={{
                fontFamily: "'VT323', monospace",
                fontSize: 28,
                lineHeight: 1.1,
                marginBottom: 24,
                textTransform: "uppercase",
              }}
            >
              IQ ANALYSIS &gt; LOGIC.DRIVEN
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 16,
                alignItems: "baseline",
                borderTop: "1px solid #FFB00040",
                borderBottom: "1px solid #FFB00040",
                padding: "16px 0",
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontFamily: "'VT323', monospace",
                  fontSize: 96,
                  lineHeight: 0.9,
                  color: "#FFB000",
                }}
              >
                100
              </div>
              <div style={{ color: "#00FFFF", fontFamily: "'VT323', monospace", fontSize: 16 }}>
                <div>OVR / 100</div>
                <div>▲ +12 vs avg</div>
                <div>BAND: GOLD</div>
              </div>
            </div>
            <button
              style={{
                background: "#FFB000",
                color: "#000",
                border: "none",
                padding: "10px 18px",
                fontFamily: "'VT323', monospace",
                fontSize: 14,
                letterSpacing: "0.1em",
                cursor: "pointer",
              }}
            >
              [F1] UNLOCK ANALYSIS
            </button>
          </Card>

          {/* B — Apple Intelligence */}
          <Card label="B · Apple Intelligence" bg="#0A0A0F" fg="#fff">
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at 80% 0%, rgba(120,140,255,0.25), transparent 50%), radial-gradient(circle at 0% 100%, rgba(255,120,200,0.15), transparent 50%)",
                pointerEvents: "none",
              }}
            />
            <div style={{ position: "relative" }}>
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#9899C9",
                  marginBottom: 16,
                }}
              >
                IQ Diagnostic · Premium
              </div>
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 26,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.15,
                  marginBottom: 22,
                  background: "linear-gradient(120deg, #fff, #B5C2FF, #fff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Logic-driven thinker.
              </div>
              <div
                style={{
                  borderRadius: 18,
                  padding: 20,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(12px)",
                  marginBottom: 20,
                }}
              >
                <div style={{ fontSize: 11, color: "#8E92B5", marginBottom: 4 }}>Overall</div>
                <div
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: 64,
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    background: "linear-gradient(135deg, #C8D6FF, #FFFFFF, #FFB6E1)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  100
                </div>
                <div style={{ fontSize: 13, color: "#9899C9", marginTop: 6 }}>
                  Higher than 98% of peers
                </div>
              </div>
              <button
                style={{
                  background: "rgba(255,255,255,0.08)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 999,
                  padding: "10px 22px",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  backdropFilter: "blur(12px)",
                }}
              >
                Unlock Detailed Analysis →
              </button>
            </div>
          </Card>

          {/* C — Brutalist Newsprint */}
          <Card label="C · Brutalist Newsprint" bg="#FFFFFF" fg="#000">
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 10,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "#000",
                fontWeight: 800,
                marginBottom: 4,
              }}
            >
              VOL.01 · ISSUE 17 · DIAGNOSTIC REPORT
            </div>
            <div
              style={{
                borderTop: "3px double #000",
                borderBottom: "1px solid #000",
                padding: "8px 0",
                marginBottom: 18,
                fontFamily: "'Inter', sans-serif",
                fontSize: 10,
                color: "#FF3B00",
                fontWeight: 700,
                letterSpacing: "0.2em",
              }}
            >
              ★ VERIFIED · NON-MEDICAL · REFERENCE ONLY
            </div>
            <div
              style={{
                fontFamily: "'Crimson Text', serif",
                fontSize: 36,
                lineHeight: 1.05,
                fontWeight: 700,
                marginBottom: 20,
                fontStyle: "italic",
              }}
            >
              "A logic-driven mind, exceptionally rare."
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 16,
                borderTop: "1px solid #000",
                borderBottom: "1px solid #000",
                padding: "16px 0",
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 96,
                  fontWeight: 900,
                  lineHeight: 0.85,
                  letterSpacing: "-0.06em",
                }}
              >
                100
              </div>
              <div style={{ fontFamily: "'Crimson Text', serif", fontSize: 14, fontStyle: "italic" }}>
                /100 — Top 2% percentile.
                <br />
                Strongest in deductive reasoning.
              </div>
            </div>
            <button
              style={{
                background: "#FF3B00",
                color: "#fff",
                border: "none",
                padding: "12px 22px",
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Read Full Analysis ▸
            </button>
          </Card>

          {/* D — Japanese Brutalism / 浮世絵テック */}
          <Card label="D · 和×テック · Japanese Brutalism" bg="#F4ECD8" fg="#1A1A1A">
            <div
              style={{
                position: "absolute",
                top: 24,
                right: 24,
                width: 72,
                height: 72,
                background: "#E60012",
                color: "#F4ECD8",
                fontFamily: "'Shippori Mincho', serif",
                fontSize: 22,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                writingMode: "vertical-rl",
                lineHeight: 1,
                letterSpacing: "0.15em",
                transform: "rotate(2deg)",
              }}
            >
              診断
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.25em",
                textTransform: "uppercase",
                color: "#8B6F4E",
                marginBottom: 12,
              }}
            >
              IQ_LOG · 第十七號
            </div>
            <div
              style={{
                fontFamily: "'Shippori Mincho', serif",
                fontSize: 38,
                lineHeight: 1.1,
                fontWeight: 800,
                letterSpacing: "0.02em",
                marginBottom: 18,
                maxWidth: "75%",
              }}
            >
              論理ドライブ型
            </div>
            <div
              style={{
                borderLeft: "4px solid #E60012",
                paddingLeft: 16,
                marginBottom: 22,
                fontFamily: "'Shippori Mincho', serif",
                fontSize: 13,
                color: "#1A1A1A99",
                lineHeight: 1.6,
              }}
            >
              論理・数的・言語の三軸が
              <br />
              すべて高水準。卓越した分析者。
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                borderTop: "1px solid #1A1A1A33",
                paddingTop: 14,
                marginBottom: 18,
              }}
            >
              <span
                style={{
                  fontFamily: "'Shippori Mincho', serif",
                  fontSize: 80,
                  fontWeight: 800,
                  lineHeight: 0.85,
                  letterSpacing: "-0.04em",
                }}
              >
                100
              </span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B6F4E" }}>
                /百点満点
              </span>
            </div>
            <button
              style={{
                background: "#1A1A1A",
                color: "#F4ECD8",
                border: "none",
                padding: "12px 22px",
                fontFamily: "'Shippori Mincho', serif",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.1em",
                cursor: "pointer",
              }}
            >
              詳細を解放する 〉
            </button>
          </Card>

          {/* E — Vaporwave / Synthwave */}
          <Card label="E · Vaporwave · Synthwave" bg="#1A0B2E" fg="#fff">
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, transparent 0%, transparent 65%, rgba(0,245,255,0.4) 65.5%, transparent 66%, transparent 70%, rgba(0,245,255,0.3) 70.5%, transparent 71%, transparent 75%, rgba(255,0,110,0.4) 75.5%, transparent 76%, transparent 80%, rgba(255,0,110,0.3) 80.5%, transparent 81%, transparent 85%, rgba(0,245,255,0.4) 85.5%, transparent 86%, transparent 90%, rgba(0,245,255,0.3) 90.5%, transparent 91%, transparent 95%, rgba(255,0,110,0.4) 95.5%, transparent 96%)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -20,
                left: "50%",
                width: 600,
                height: 200,
                transform: "translateX(-50%) perspective(400px) rotateX(60deg)",
                background:
                  "repeating-linear-gradient(90deg, transparent 0, transparent 24px, rgba(0,245,255,0.4) 24px, rgba(0,245,255,0.4) 25px), repeating-linear-gradient(0deg, transparent 0, transparent 24px, rgba(255,0,110,0.3) 24px, rgba(255,0,110,0.3) 25px)",
                pointerEvents: "none",
                opacity: 0.6,
              }}
            />
            <div style={{ position: "relative" }}>
              <div
                style={{
                  fontFamily: "'Major Mono Display', monospace",
                  fontSize: 11,
                  letterSpacing: "0.3em",
                  color: "#00F5FF",
                  textShadow: "0 0 8px rgba(0,245,255,0.8)",
                  marginBottom: 18,
                }}
              >
                ▼ IQ.LOG // 198X
              </div>
              <div
                style={{
                  fontFamily: "'Audiowide', cursive",
                  fontSize: 30,
                  lineHeight: 1.1,
                  background: "linear-gradient(180deg, #fff, #FF006E)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 0 24px rgba(255,0,110,0.5)",
                  marginBottom: 22,
                  letterSpacing: "0.05em",
                }}
              >
                LOGIC DRIVEN
              </div>
              <div
                style={{
                  fontFamily: "'Audiowide', cursive",
                  fontSize: 88,
                  background: "linear-gradient(180deg, #fff 0%, #00F5FF 50%, #FF006E 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 0 16px rgba(255,0,110,0.6))",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                100
              </div>
              <div
                style={{
                  fontFamily: "'Major Mono Display', monospace",
                  fontSize: 12,
                  color: "#FF006E",
                  letterSpacing: "0.2em",
                  marginBottom: 22,
                }}
              >
                ░ ░ ░ TOP 2% ░ ░ ░
              </div>
              <button
                style={{
                  background: "linear-gradient(135deg, #FF006E, #00F5FF)",
                  color: "#fff",
                  border: "none",
                  padding: "12px 22px",
                  fontFamily: "'Audiowide', cursive",
                  fontSize: 13,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  boxShadow: "0 0 24px rgba(255,0,110,0.6)",
                  cursor: "pointer",
                }}
              >
                ▶ Unlock
              </button>
            </div>
          </Card>

          {/* Current — for comparison */}
          <Card label="(Current) · Obsidian × Lime" bg="#060809" fg="#fff">
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.25em",
                color: "#CCFF00",
                marginBottom: 18,
              }}
            >
              // IQ_DIAGNOSTIC · PREMIUM
            </div>
            <div
              style={{
                fontFamily: "Anton, sans-serif",
                fontSize: 30,
                letterSpacing: "0.01em",
                textTransform: "uppercase",
                marginBottom: 22,
                lineHeight: 1.1,
              }}
            >
              論理ドライブ型
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 96,
                color: "#CCFF00",
                letterSpacing: "-0.02em",
                lineHeight: 0.9,
                textShadow: "0 0 24px rgba(204,255,0,0.5)",
                marginBottom: 22,
              }}
            >
              100
            </div>
            <button
              style={{
                background: "#CCFF00",
                color: "#000",
                border: "none",
                padding: "12px 22px",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: "0 0 20px rgba(204,255,0,0.4)",
              }}
            >
              Unlock →
            </button>
          </Card>

          {/* F — NASA Mission Control */}
          <Card label="F · NASA Mission Control" bg="#0A1628" fg="#fff">
            <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, letterSpacing: "0.2em", color: "#FF9F1C", marginBottom: 8 }}>
              MISSION-IQ-017 · STATUS: NOMINAL
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, marginBottom: 18 }}>
              {["LOGIC", "NUMERIC", "VERBAL"].map((t) => (
                <div key={t} style={{ background: "#000", border: "1px solid #2DD4BF", padding: "6px 8px", fontFamily: "'Roboto Mono', monospace", fontSize: 9, color: "#2DD4BF", letterSpacing: "0.15em" }}>
                  {t} ✓
                </div>
              ))}
            </div>
            <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 22, lineHeight: 1.2, marginBottom: 18, fontWeight: 500 }}>
              IQ ANALYSIS // PROFILE<br />
              <span style={{ color: "#FF9F1C" }}>LOGIC.DRIVEN</span>
            </div>
            <div style={{ background: "#000", border: "1px solid #FF9F1C40", padding: "12px 14px", marginBottom: 18, fontFamily: "'Roboto Mono', monospace" }}>
              <div style={{ fontSize: 10, color: "#888", letterSpacing: "0.2em", marginBottom: 4 }}>PRIMARY READOUT</div>
              <div style={{ fontSize: 64, color: "#FF9F1C", lineHeight: 0.9, fontWeight: 700, letterSpacing: "-0.02em" }}>
                100<span style={{ fontSize: 16, color: "#666" }}> /100</span>
              </div>
              <div style={{ fontSize: 9, color: "#2DD4BF", marginTop: 6 }}>▲ +0.12σ above mean · BAND.GOLD</div>
            </div>
            <button style={{ background: "transparent", color: "#2DD4BF", border: "1px solid #2DD4BF", padding: "10px 18px", fontFamily: "'Roboto Mono', monospace", fontSize: 11, letterSpacing: "0.15em", cursor: "pointer" }}>
              [SYS] EXECUTE UNLOCK
            </button>
          </Card>

          {/* G — Editorial / Vogue */}
          <Card label="G · Editorial · Vogue" bg="#FFFFFF" fg="#000">
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: "0.4em", color: "#666", fontWeight: 600, marginBottom: 8 }}>
              IQ LOG · MAY 2026
            </div>
            <div style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 64, lineHeight: 0.9, fontWeight: 900, letterSpacing: "-0.04em", marginBottom: 12 }}>
              The<br />
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>analyst</span>.
            </div>
            <div style={{ width: 60, height: 1, background: "#000", margin: "20px 0" }} />
            <div style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 14, fontStyle: "italic", lineHeight: 1.5, color: "#333", marginBottom: 22 }}>
              "Sharp, methodical, three-axis<br />high-band cognition."
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 20 }}>
              <span style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 88, fontWeight: 900, lineHeight: 0.85, letterSpacing: "-0.06em" }}>100</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: "0.3em", color: "#888" }}>
                /100<br />TOP 2%
              </span>
            </div>
            <button style={{ background: "transparent", color: "#000", border: "none", borderBottom: "2px solid #000", padding: "8px 0", fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", cursor: "pointer" }}>
              Read the full report →
            </button>
          </Card>

          {/* H — Y2K / Chrome / Glitch */}
          <Card label="H · Y2K · Chrome · Glitch" bg="#E8E8FF" fg="#000">
            <div style={{ position: "absolute", inset: 0, background: "repeating-conic-gradient(#FF00CC 0deg 18deg, #00FFFF 18deg 36deg)", opacity: 0.08, pointerEvents: "none" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Press Start 2P', monospace", fontSize: 9, color: "#FF00CC", marginBottom: 16, letterSpacing: "0.05em" }}>
                ★ IQ.LOG v2.0 ★
              </div>
              <div style={{ fontFamily: "'Press Start 2P', monospace", fontSize: 18, lineHeight: 1.3, color: "#000", textShadow: "3px 3px 0 #FF00CC, 6px 6px 0 #00FFFF", marginBottom: 26 }}>
                LOGIC<br />DRIVEN!
              </div>
              <div style={{ display: "inline-block", background: "linear-gradient(180deg, #ffffff 0%, #C0C0FF 50%, #6060A0 51%, #C0C0FF 100%)", padding: "10px 20px", border: "2px solid #000", boxShadow: "4px 4px 0 #000", marginBottom: 22 }}>
                <span style={{ fontFamily: "'Press Start 2P', monospace", fontSize: 36, color: "#000", textShadow: "2px 2px 0 #FF00CC" }}>100</span>
              </div>
              <div style={{ fontFamily: "'Press Start 2P', monospace", fontSize: 8, color: "#0000CC", marginBottom: 18 }}>
                ♥ TOP 2% ♥ GOLD ♥
              </div>
              <button style={{ background: "#FF00CC", color: "#fff", border: "2px solid #000", padding: "10px 18px", fontFamily: "'Press Start 2P', monospace", fontSize: 9, cursor: "pointer", boxShadow: "3px 3px 0 #00FFFF" }}>
                ▶ UNLOCK!!
              </button>
            </div>
          </Card>

          {/* I — Holographic Tron */}
          <Card label="I · Holographic · Tron" bg="#000000" fg="#00E5FF">
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 0%, rgba(0,229,255,0.06) 50%, transparent 100%)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(0,229,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.12) 1px, transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: "0.3em", color: "#00E5FF", fontWeight: 700, marginBottom: 18 }}>
                ◇ IQ_LOG / DIAGNOSTIC GRID
              </div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 26, lineHeight: 1.2, fontWeight: 900, color: "#fff", textShadow: "0 0 12px rgba(0,229,255,0.8)", letterSpacing: "0.05em", marginBottom: 22 }}>
                LOGIC.DRIVEN
              </div>
              <div style={{ border: "1px solid #00E5FF", padding: 18, marginBottom: 22, boxShadow: "0 0 24px rgba(0,229,255,0.4), inset 0 0 24px rgba(0,229,255,0.08)", clipPath: "polygon(0 0, 100% 0, 100% 70%, 92% 100%, 0 100%)" }}>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 72, color: "#fff", textShadow: "0 0 16px rgba(0,229,255,0.9)", lineHeight: 0.9, fontWeight: 900, letterSpacing: "-0.02em" }}>
                  100
                </div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, color: "#00E5FF", letterSpacing: "0.3em", marginTop: 4 }}>
                  ▲ 0.98 PERCENTILE
                </div>
              </div>
              <button style={{ background: "transparent", color: "#00E5FF", border: "1px solid #00E5FF", padding: "10px 22px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.25em", textTransform: "uppercase", cursor: "pointer", boxShadow: "0 0 12px rgba(0,229,255,0.5)" }}>
                Initialize ⟶
              </button>
            </div>
          </Card>

          {/* J — Tokyo Neon Signage */}
          <Card label="J · 東京ネオン · Tokyo Signage" bg="#0A0A0A" fg="#fff">
            <div style={{ position: "absolute", top: 24, right: 20, fontFamily: "'Noto Sans JP', sans-serif", fontSize: 13, fontWeight: 900, color: "#FFE600", writingMode: "vertical-rl", letterSpacing: "0.4em", lineHeight: 1, textShadow: "0 0 8px rgba(255,230,0,0.6)" }}>
              診 · 断 · 結 · 果
            </div>
            <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.25em", color: "#FF1744", textShadow: "0 0 6px rgba(255,23,68,0.6)", marginBottom: 14 }}>
              ▍ IQ ロ グ
            </div>
            <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 44, fontWeight: 900, lineHeight: 1.0, letterSpacing: "-0.02em", color: "#fff", marginBottom: 8, maxWidth: 240 }}>
              論理<br />
              <span style={{ color: "#FF1744", textShadow: "0 0 14px rgba(255,23,68,0.7)" }}>ドライブ</span><br />型。
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 800, letterSpacing: "0.3em", color: "#666", marginBottom: 18 }}>
              LOGIC-DRIVEN ANALYST
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, borderTop: "2px solid #FF1744", paddingTop: 12, marginBottom: 18 }}>
              <span style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 80, fontWeight: 900, color: "#FFE600", textShadow: "0 0 18px rgba(255,230,0,0.5)", lineHeight: 0.85, letterSpacing: "-0.04em" }}>
                100
              </span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: "#888", letterSpacing: "0.2em" }}>
                /100<br />TOP 2%
              </span>
            </div>
            <button style={{ background: "#FF1744", color: "#fff", border: "none", padding: "12px 22px", fontFamily: "'Noto Sans JP', sans-serif", fontSize: 13, fontWeight: 900, letterSpacing: "0.15em", cursor: "pointer", boxShadow: "0 0 18px rgba(255,23,68,0.6)" }}>
              ▶ 解放する
            </button>
          </Card>

          {/* K — Linear / Modern SaaS Violet */}
          <Card label="K · Modern SaaS · Linear style" bg="#0F0F14" fg="#fff">
            <div style={{ position: "absolute", top: 0, right: 0, width: 300, height: 300, background: "radial-gradient(circle at top right, rgba(139,92,246,0.25), transparent 60%)", pointerEvents: "none" }} />
            <div style={{ position: "relative" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 11, color: "#A78BFA", fontWeight: 600, marginBottom: 18 }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#A78BFA" }} />
                IQ Diagnostic · Result
              </div>
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2, marginBottom: 6 }}>
                Logic-driven thinker
              </div>
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#9CA3AF", marginBottom: 22 }}>
                Strong across logic, numeric, and verbal dimensions.
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 6 }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 56, fontWeight: 800, letterSpacing: "-0.04em", background: "linear-gradient(135deg, #fff, #A78BFA)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  100
                </span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "#6B7280" }}>/100</span>
              </div>
              <div style={{ height: 4, background: "#1F2030", borderRadius: 999, overflow: "hidden", marginBottom: 22 }}>
                <div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, #8B5CF6, #EC4899)" }} />
              </div>
              <button style={{ background: "linear-gradient(135deg, #8B5CF6, #6366F1)", color: "#fff", border: "none", padding: "10px 18px", borderRadius: 8, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 12px rgba(139,92,246,0.35)" }}>
                Unlock detailed analysis →
              </button>
            </div>
          </Card>

        </div>
      </div>
    </>
  );
}
